import {
	useAuthenticationMethods,
	useCustomProviders,
	useUpdateProvidersMutation,
	useUpdateSettingsMutation
} from '@api/authMethods';
import API_ROUTES from '@api/routes';
import AppHeader from '@components/AppNavigation/AppHeader';
import {
	AuthMethodListItem,
	EnchantedLinkPage,
	MagicLinkPage,
	OTPPage,
	SocialPage,
	SSOPage,
	TOTPPage,
	WebAuthnPage,
	PasswordPage
} from '@components/AuthMethods';
import { PasswordOutlined } from '@mui/icons-material';

import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LockClockIcon from '@mui/icons-material/LockClock';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, List } from '@mui/material';
import { useTheme } from '@mui/system';
import { APP_ROUTES, AuthMethodRoute } from 'constants/routes';
import { useNavigate } from 'hooks';
import { MessageConnectorProvider } from 'hooks/useConnectorsContext';
import React, { FC } from 'react';
import {
	IConnectorDefaults,
	IConnectors,
	IDefaultConnectorProps
} from 'types/connectors';
import {
	IAuthMethodsSettings,
	IMagicLinkSettings,
	IOIDCSettings,
	IOTPSettings,
	IPasswordSettings,
	ISocialSettings,
	ISSOSettings,
	ITOTPSettings,
	IWebAuthnSettings
} from 'types/settings';
import { ITheme } from 'types/theme';

interface IAuthenticationMethodsPage {
	authPage: AuthMethodRoute;
}

const AuthenticationMethodsPage: FC<IAuthenticationMethodsPage> = ({
	authPage
}) => {
	const navigate = useNavigate();
	const theme = useTheme<ITheme>();
	const { isLoading: isLoadingSettings, data } = useAuthenticationMethods();
	const res = useCustomProviders();
	const [tpls, providers] = res.map((r) => r.data);
	const isLoadingProviders = res.some((r) => r.isLoading);
	const isLoading = isLoadingSettings && isLoadingProviders;

	const settings = data as IAuthMethodsSettings;
	const templates = tpls as IConnectorDefaults;
	const connectors = providers as IConnectors;

	const providersMutation = useUpdateProvidersMutation();

	const withConnectorProvider = (page: React.ReactElement) => (
		<MessageConnectorProvider
			pageId={authPage}
			customConnectors={connectors}
			defaultTemplates={templates}
			onSaveConnectors={providersMutation.mutateAsync}
		>
			{page}
		</MessageConnectorProvider>
	);

	const otpMutation = useUpdateSettingsMutation<IOTPSettings>(
		API_ROUTES.v1.authenticationMethods.otp,
		(authSettings, newData) => {
			authSettings.otp = newData;
		}
	);
	const magicLinkMutation = useUpdateSettingsMutation<IMagicLinkSettings>(
		API_ROUTES.v1.authenticationMethods.magicLink,
		(authSettings, newData) => {
			authSettings.magiclink = newData;
		}
	);
	const enchantedLinkMutation = useUpdateSettingsMutation<IMagicLinkSettings>(
		API_ROUTES.v1.authenticationMethods.enchantedLink,
		(authSettings, newData) => {
			authSettings.enchantedlink = newData;
		}
	);
	const socialMutation = useUpdateSettingsMutation<ISocialSettings>(
		API_ROUTES.v1.authenticationMethods.social,
		(authSettings, newData) => {
			authSettings.oauth = newData;
		}
	);
	const webauthnMutation = useUpdateSettingsMutation<IWebAuthnSettings>(
		API_ROUTES.v1.authenticationMethods.webauthn,
		(authSettings, newData) => {
			authSettings.webauthn = newData;
		}
	);
	const totpMutation = useUpdateSettingsMutation<ITOTPSettings>(
		API_ROUTES.v1.authenticationMethods.totp,
		(authSettings, newData) => {
			authSettings.totp = newData;
		}
	);

	const ssoMutation = useUpdateSettingsMutation<ISSOSettings>(
		API_ROUTES.v1.authenticationMethods.saml,
		(authSettings, newData) => {
			authSettings.saml = newData;
		}
	);

	const oidcMutation = useUpdateSettingsMutation<IOIDCSettings>(
		API_ROUTES.v1.authenticationMethods.oidc,
		(authSettings, newData) => {
			authSettings.oidc = newData;
		}
	);

	const passwordMutation = useUpdateSettingsMutation<IPasswordSettings>(
		API_ROUTES.v1.authenticationMethods.password,
		(authSettings, newData) => {
			authSettings.password = newData;
		}
	);

	let page;
	switch (authPage) {
		case AuthMethodRoute.OTP:
			page = withConnectorProvider(
				<OTPPage
					settings={settings?.otp}
					templates={templates?.otp as IDefaultConnectorProps}
					onSaveConnectors={providersMutation.mutateAsync}
					onSaveSettings={otpMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.MagicLink:
			page = withConnectorProvider(
				<MagicLinkPage
					settings={settings?.magiclink}
					templates={templates?.magiclink as IDefaultConnectorProps}
					onSaveConnectors={providersMutation.mutateAsync}
					onSaveSettings={magicLinkMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.EnchantedLink:
			page = withConnectorProvider(
				<EnchantedLinkPage
					settings={settings?.enchantedlink}
					templates={templates?.enchantedlink as IDefaultConnectorProps}
					onSaveSettings={enchantedLinkMutation.mutateAsync}
					onSaveConnectors={providersMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.Password:
			page = withConnectorProvider(
				<PasswordPage
					settings={settings?.password}
					templates={templates?.password as IDefaultConnectorProps}
					onSaveSettings={passwordMutation.mutateAsync}
					onSaveConnectors={providersMutation.mutateAsync}
					activeAuthMethods={{
						enchantedlink: settings?.enchantedlink?.enabled || false,
						magiclink: settings?.magiclink?.enabled || false
					}}
				/>
			);
			break;

		case AuthMethodRoute.Social:
			page = (
				<SocialPage
					settings={settings?.oauth}
					onSocialSave={socialMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.SSO:
			page = (
				<SSOPage
					saml={settings?.saml}
					oidc={settings?.oidc}
					onSPSave={ssoMutation.mutateAsync}
					onOIDCSave={oidcMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.WebAuthn:
			page = (
				<WebAuthnPage
					settings={settings?.webauthn}
					onSave={webauthnMutation.mutateAsync}
				/>
			);
			break;
		case AuthMethodRoute.TOTP:
			page = (
				<TOTPPage settings={settings?.totp} onSave={totpMutation.mutateAsync} />
			);
			break;
		default:
			break;
	}

	const handleListItemChanged = React.useCallback(
		(route: string) => {
			navigate(route);
		},
		[navigate]
	);

	return (
		<Box
			display="flex"
			flexGrow={1}
			flexDirection="row"
			data-testid="authentication-methods-page"
			overflow="hidden"
		>
			<Box display="flex" flexDirection="column">
				<AppHeader translation="pages.authenticationMethods.title" />
				<List>
					<AuthMethodListItem
						icon={<LockClockIcon color="primary" />}
						onClick={() => handleListItemChanged(APP_ROUTES.authMethodsOTP)}
						loading={isLoading}
						selected={authPage === AuthMethodRoute.OTP}
						authMethod={AuthMethodRoute.OTP}
					/>
					<AuthMethodListItem
						icon={<AutoAwesome color="primary" />}
						loading={isLoading}
						selected={authPage === AuthMethodRoute.MagicLink}
						authMethod={AuthMethodRoute.MagicLink}
						onClick={() =>
							handleListItemChanged(APP_ROUTES.authMethodsMagicLink)
						}
					/>
					<AuthMethodListItem
						icon={<AutoFixHigh color="primary" />}
						loading={isLoading}
						selected={authPage === AuthMethodRoute.EnchantedLink}
						authMethod={AuthMethodRoute.EnchantedLink}
						onClick={() =>
							handleListItemChanged(APP_ROUTES.authMethodsEnchantedLink)
						}
					/>
					<AuthMethodListItem
						icon={<AssignmentIndIcon color="primary" />}
						authMethod={AuthMethodRoute.Social}
						loading={isLoading}
						onClick={() => handleListItemChanged(APP_ROUTES.authMethodsSocial)}
						selected={authPage === AuthMethodRoute.Social}
					/>
					<AuthMethodListItem
						icon={<VpnKeyIcon color="primary" />}
						authMethod={AuthMethodRoute.SSO}
						loading={isLoading}
						onClick={() => handleListItemChanged(APP_ROUTES.authMethodsSSO)}
						selected={authPage === AuthMethodRoute.SSO}
					/>
					<AuthMethodListItem
						icon={<FingerprintIcon color="primary" />}
						authMethod={AuthMethodRoute.WebAuthn}
						loading={isLoading}
						onClick={() =>
							handleListItemChanged(APP_ROUTES.authMethodsWebAuthn)
						}
						selected={authPage === AuthMethodRoute.WebAuthn}
					/>
					<AuthMethodListItem
						icon={<TimelapseIcon color="primary" />}
						authMethod={AuthMethodRoute.TOTP}
						loading={isLoading}
						onClick={() => handleListItemChanged(APP_ROUTES.authMethodsTOTP)}
						selected={authPage === AuthMethodRoute.TOTP}
					/>
					<AuthMethodListItem
						icon={<PasswordOutlined color="primary" />}
						authMethod={AuthMethodRoute.Password}
						loading={isLoading}
						onClick={() =>
							handleListItemChanged(APP_ROUTES.authMethodsPassword)
						}
						selected={authPage === AuthMethodRoute.Password}
					/>
				</List>
			</Box>
			<Box
				data-testid="authentication-methods-content"
				display="flex"
				flexDirection="column"
				flexGrow={1}
				borderLeft={1}
				borderColor={theme.palette.divider}
			>
				{page}
			</Box>
		</Box>
	);
};

export default AuthenticationMethodsPage;
